// =============================================
// docSearch — what the Process Docs search box matches a document against
// =============================================
// Owner (2026-10-17): one box that finds a document by anything about it — file name,
// tags, vendor (and that vendor's alias names), amount, reference number, date, type,
// folder, owner, notes. Every word typed must match somewhere ("clearview 68.55" finds
// that one receipt). Words printed INSIDE the document are found server-side
// (/api/dms/extract-search) and merged by the caller.
// Pure — unit-tested in docSearch.test.ts.
// =============================================

export interface DocSearchFields {
  texts: (string | null | undefined)[];          // name, party, ref, purpose, notes, type, folders, owners, aliases…
  amounts: (number | string | null | undefined)[];
  dates: (string | null | undefined)[];          // YYYY-MM-DD…
}

/** The words of a query: lower-case, split on spaces (quotes keep a phrase together). */
export function searchTerms(query: string): string[] {
  const out: string[] = [];
  const re = /"([^"]+)"|(\S+)/g;
  let m: RegExpExecArray | null;
  const q = String(query || '').toLowerCase();
  while ((m = re.exec(q))) {
    const t = (m[1] || m[2] || '').trim();
    if (t) out.push(t);
  }
  return out;
}

/** "$1,234.5" → "1234.5"; '' when the word is not an amount. */
export function amountQuery(term: string): string {
  const t = String(term || '').trim().replace(/^[-+]?\$/, '').replace(/,/g, '').replace(/\$$/, '');
  return /^\d+(\.\d{0,2})?$/.test(t) ? t : '';
}

/** Does an amount match what was typed? "45" = 45.00–45.99; "45.2" = 45.20–45.29; "45.20" = 45.20. */
export function amountMatches(amount: number | string | null | undefined, term: string): boolean {
  const q = amountQuery(term);
  if (!q || amount == null || amount === '') return false;
  const n = Number(amount);
  if (!Number.isFinite(n)) return false;
  const fixed = Math.abs(n).toFixed(2);
  if (!q.includes('.')) return fixed.split('.')[0] === q;
  const [qi, qd] = q.split('.');
  const [ai, ad] = fixed.split('.');
  return ai === qi && ad.startsWith(qd);
}

/** Does a date match? "2025-10-17", "2025-10", "2025/10/17", "10-17" (any part, in order). */
export function dateMatches(date: string | null | undefined, term: string): boolean {
  const d = String(date || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
  const t = String(term || '').trim().replace(/[/.]/g, '-');
  if (!/^\d{1,4}(-\d{1,2}){0,2}$/.test(t) || !t.includes('-')) return false;   // a bare number is an amount / ref, not a date
  return d.includes(t);
}

/** Every word of the query matches one of the document's texts, amounts or dates. */
export function docMatchesSearch(fields: DocSearchFields, query: string): boolean {
  const terms = searchTerms(query);
  if (!terms.length) return true;
  const texts = fields.texts.map(v => String(v == null ? '' : v).toLowerCase()).filter(Boolean);
  return terms.every(t =>
    texts.some(x => x.includes(t))
    || fields.amounts.some(a => amountMatches(a, t))
    || fields.dates.some(d => dateMatches(d, t)));
}
